import { useCallback } from 'react';
import { formatToOneDecimal, getCurrentAbsenceBaseYear } from '@/services/utils';

import type { AbsenceValues } from '@/types/absence';

interface AbsenceInfo {
	rates_by_year?: Record<number, number | string>;
}

interface ValueProps {
	index: number;
	loading: boolean;
	changedAbsenceValues: AbsenceValues;
	absenceInfo: AbsenceInfo;
}

const Value = ({ index, loading, changedAbsenceValues, absenceInfo }: ValueProps) => {
	// Calculate the year for this index
	const year = getCurrentAbsenceBaseYear() - index;

	// Helper function to get absence rate by year
	const getAbsenceRateByYear = useCallback(
		(year: number): string | number | null => {
			return absenceInfo?.rates_by_year?.[year] ?? null;
		},
		[absenceInfo]
	);

	// Get the display value with fallback logic
	const getDisplayValue = useCallback((): string => {
		// In loading state, prefer changed values, then fall back to original rates
		if (loading) {
			const changedValue = formatToOneDecimal(changedAbsenceValues[`vz${index}`]);
			if (changedValue) return changedValue;

			const originalValue = formatToOneDecimal(getAbsenceRateByYear(year));
			if (originalValue) return originalValue;

			return ' - ';
		}

		// In normal state, only show original rates
		return formatToOneDecimal(getAbsenceRateByYear(year)) || ' - ';
	}, [loading, changedAbsenceValues, index, year, getAbsenceRateByYear]);

	return (
		// `translate="no"` keeps auto-translate from mutating the numeric text node, and
		// wrapping the value in its own span gives React a stable text-node boundary
		// separate from the `%` so reconciliation can't collapse it to " - ". See issue #747.
		<span className="text-foreground" data-testid={`absence-value-${index}`} translate="no">
			<span>{getDisplayValue()}</span>%
		</span>
	);
};
export default Value;
